import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Building, Mail, Phone, Clock, Target, Trash2, Edit } from "lucide-react";
import { StatusBadge } from "@/components/ui/status-badge";
import { TagBadge } from "@/components/ui/tag-badge";
import { CareerHistory } from "./CareerHistory";
import { ActivityFeed } from "./ActivityFeed";
import { ProposalsList } from "./ProposalsList";
import { ProjectsList } from "./ProjectsList";
import { PersonOverview } from "./PersonOverview";

type Person = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  status: string | null;
  tags: string[] | null;
  is_decision_maker: boolean | null;
  notes: string | null;
  clients?: {
    name: string;
  };
};

interface PersonDetailsProps {
  person: Person;
  onEdit?: (person: Person) => void;
  onDelete?: (personId: string) => void;
  isDeleting: boolean;
  getLastActivity: (personId: string) => string;
}

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "career", label: "Career History" },
  { id: "activities", label: "Activities" },
  { id: "proposals", label: "Proposals" },
  { id: "projects", label: "Projects Involved" },
] as const;

type TabId = typeof tabs[number]["id"];

export function PersonDetails({ person, onEdit, onDelete, isDeleting, getLastActivity }: PersonDetailsProps) {
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return <PersonOverview personId={person.id} />;
      case "career":
        return <CareerHistory personId={person.id} />;
      case "activities":
        return <ActivityFeed personId={person.id} />;
      case "proposals":
        return <ProposalsList personId={person.id} />;
      case "projects":
        return <ProjectsList personId={person.id} />;
      default:
        return <PersonOverview personId={person.id} />;
    }
  };
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">{person.first_name} {person.last_name}</h1>
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Building className="h-4 w-4" />
              <span className="hover:text-primary cursor-pointer">{person.clients?.name || "No company"}</span>
              <span>•</span>
              <span>{person.position}</span>
              {person.is_decision_maker && (
                <>
                  <span>•</span>
                  <div className="flex items-center text-primary">
                    <Target className="h-4 w-4 mr-1" />
                    <span>Decision Maker</span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <StatusBadge status={person.status || ""} />
            {onEdit && (
              <Button variant="outline" size="sm" onClick={() => onEdit(person)}>
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Person</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete {person.first_name} {person.last_name}? 
                      This action cannot be undone and will also delete all related employment history, activities, and proposal recipients.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDelete(person.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Contact info and tags */}
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{person.email}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{person.phone}</span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Last activity: {getLastActivity(person.id)}</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium mb-2">Tags</div>
            <div className="flex flex-wrap gap-2">
              {(person.tags || []).map((tag) => (
                <TagBadge key={tag} tag={tag} />
              ))}
            </div>
          </div>
        </div>

        {/* Notes */}
        {person.notes && (
          <div className="bg-muted/50 p-4 rounded-lg mb-6">
            <div className="text-sm font-medium mb-1">Notes</div>
            <div className="text-sm text-muted-foreground">{person.notes}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="w-full">
        {/* Tab navigation */}
        <div className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
                activeTab === tab.id
                  ? "bg-background text-foreground shadow-sm"
                  : "hover:bg-background/80"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="mt-2">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}